import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Button,
  message,
  Select,
  Space,
  Spin,
  Typography,
  Card,
  Row,
  Col,
  Divider,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { SearchOutlined, UserOutlined, BankOutlined } from '@ant-design/icons';
import API from 'utils/api';
import { useToken } from 'app/hooks/auth/token';
import { API_URL } from 'app/constants/APIKEYS';
import { useGroupFee } from 'app/hooks/adminUsers/groupFee';

const { Text, Title } = Typography;
const { Option } = Select;

interface UserData {
  id: number;
  uid?: string;
  email: string;
  nickname?: string;
  fullname?: string;
  phone?: string;
  level: number;
  balance?: number;
}

interface KycData {
  id: string;
  status: number;
  userData: {
    bankName: string;
    accountNumber: string;
    accountHolder: string;
    phoneNumber: string;
  };
}

interface ManualWithdrawalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ManualWithdrawalModal: React.FC<ManualWithdrawalModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { data: token } = useToken();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [kycData, setKycData] = useState<KycData | null>(null);
  const [usdtPrice, setUsdtPrice] = useState(0);
  const [inputUserId, setInputUserId] = useState<string>('');
  const { data: groupData } = useGroupFee();
  const watchedKrwAmount = Form.useWatch('krwAmount', form);
  const watchedUsdtAmount = Form.useWatch('usdtAmount', form);

  // Supra API 하드코딩된 인증키
  const SUPRA_AUTH_KEY = 'b86e354183106eb71c5884405f0d4788f962e674fefabf90ab872841a8326d9e';

  // 레벨 명칭 가져오기 함수
  const getLevelName = (level: number) => {
    const levelGroup = groupData?.find((group: any) => group.level === level);
    return levelGroup?.name?.replace(/Users|회원/gi, '') || `Level ${level}`;
  };

  // USDT 가격 가져오기 (개선된 버전)
  const getBithumbUSDTPrice = async () => {
    try {
      const response = await fetch('https://api.bithumb.com/public/ticker/USDT_KRW');
      const data = await response.json();
      
      if (data.status === '0000') {
        const priceInfo = {
          price: parseFloat(data.data.closing_price),
          timestamp: parseInt(data.data.date)
        };
        setUsdtPrice(priceInfo.price);
        return priceInfo;
      }
    } catch (error) {
      console.error('USDT 가격 조회 실패:', error);
    }
  };

  // 원화를 USDT로 변환하는 함수 (정수로 반올림)
  const convertKrwToUsdt = (krwAmount: number) => {
    if (!usdtPrice || usdtPrice <= 0) return 0;
    return Math.round(krwAmount / usdtPrice);
  };

  // USDT를 원화로 변환하는 함수
  const convertUsdtToKrw = (usdtAmount: number) => {
    if (!usdtPrice || usdtPrice <= 0) return 0;
    return usdtAmount * usdtPrice;
  };

  // 원화 입력 시 USDT 자동 계산
  const handleKrwAmountChange = (value: number | null) => {
    if (value && usdtPrice > 0) {
      const usdtAmount = convertKrwToUsdt(value);
      form.setFieldsValue({ usdtAmount: usdtAmount });
    } else {
      form.setFieldsValue({ usdtAmount: undefined });
    }
  };

  // USDT 입력 시 원화 자동 계산
  const handleUsdtAmountChange = (value: number | null) => {
    if (value && usdtPrice > 0) {
      const krwAmount = convertUsdtToKrw(value);
      form.setFieldsValue({ krwAmount: Math.round(krwAmount) });
    } else {
      form.setFieldsValue({ krwAmount: undefined });
    }
  };

  // 사용자 정보 조회
  const handleUserSearch = async () => {
    const userId = parseInt(inputUserId);
    if (!userId || userId <= 0) {
      message.error('올바른 사용자 ID를 입력해주세요.');
      return;
    }

    setSearchLoading(true);
    try {
      // 1. 실제 사용자 정보 조회 (AdminUserModal에서 사용하는 API)
      const userResponse = await API.get(
        `/admin/user/${userId}`,
        token + '',
        {}
      );

      if (!userResponse.result?.success) {
        message.error(`사용자 ID ${userId}를 찾을 수 없습니다.`);
        return;
      }

      const userData = userResponse.result.result;

      // 2. 지갑 잔액 조회 (admin/walletlist API 사용)
      const walletResponse = await API.get(
        `/admin/walletlist?userid=${userId}&maxData=20&page=1`,
        token + '',
        {}
      );

      let spotBalance = 0;
      if (walletResponse.result?.success && walletResponse.result.result?.list) {
        const spotWallet = walletResponse.result.result.list.find((wallet: any) => wallet.wallet_type === 0);
        if (spotWallet) {
          spotBalance = parseFloat(spotWallet.balance || 0);
        }
      }

      // 3. 실제 사용자 정보로 설정
        const userInfo: UserData = {
          id: userData.id,
          uid: userData.uid || String(userData.id),
          email: userData.email,
          level: userData.level,
          balance: spotBalance,
          nickname: userData.nickname,
          fullname: userData.fullname,
          phone: userData.phone,
        };

      setSelectedUser(userInfo);
      form.setFieldsValue({ userId });

        // KYC 데이터를 사용자 정보에서 추출
        if (userData.kycData) {
          const kycInfo: KycData = {
            id: String(userData.id),
            status: 2, // KYC 데이터가 있으면 인증된 것으로 간주
            userData: {
              bankName: userData.kycData.bankName || '',
              accountNumber: userData.kycData.accountNumber || '',
              accountHolder: userData.kycData.accountHolder || userData.kycData.name || userData.fullname || '',
              phoneNumber: userData.kycData.phoneNumber || userData.phone || '',
            }
          };
          setKycData(kycInfo);
        } else {
          setKycData(null);
        }

    } catch (error) {
      console.error('사용자 정보 조회 실패:', error);
      message.error('사용자 정보 조회에 실패했습니다.');
    } finally {
      setSearchLoading(false);
    }
  };

  // 출금 요청 처리
  const handleSubmit = async (values: any) => {
    if (!selectedUser || !token) {
      message.error('먼저 사용자 정보를 조회해주세요.');
      return;
    }

    // 원화 금액이 입력되었는지 확인
    if (!values.krwAmount || values.krwAmount < 10000) {
      message.error('최소 출금 금액은 10,000원입니다.');
      return;
    }

    // USDT 금액 계산
    const usdtAmount = convertKrwToUsdt(values.krwAmount);
    if (usdtAmount < 10) {
      message.error('변환된 USDT 금액이 최소 출금 금액(10 USDT)보다 작습니다.');
      return;
    }

    // USDT 잔액 확인
    if (selectedUser.balance && usdtAmount > Math.floor(selectedUser.balance)) {
      message.error(`출금 가능한 USDT가 부족합니다. (잔액: ${Math.floor(selectedUser.balance).toLocaleString()} USDT, 필요: ${usdtAmount.toLocaleString()} USDT)`);
      return;
    }

    setLoading(true);
    try {
      // 1. Supra API 호출 (수수료 -1 차감)
      const supaResponse = await fetch('https://api-v2.supra-pay.com/withdrawal/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization-Key': SUPRA_AUTH_KEY,
        },
        body: JSON.stringify({
          userid: `${selectedUser.id}`,
          request_type: 'user',
          qty: `${usdtAmount - 1}`, // 수수료로 -1 차감
          wallet_address: '',
          transfer_type: 'otc',
        }),
      });

      const supaData = await supaResponse.json();

      if (!supaResponse.ok) {
        if (supaData.detail && supaData.detail.includes('pending withdrawal request')) {
          throw new Error('대기 중인 출금 요청이 있습니다. 처리가 완료될 때까지 기다려주세요.');
        }
        throw new Error(supaData.message || 'Supra API 호출 실패');
      }

      // 2. 내부 API 호출 (기존 출금 요청 API 사용)
      const response = await fetch(`${API_URL}/transfer/admin/withdrawal/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: usdtAmount,
          userId: selectedUser.id, // 관리자가 대신 요청하는 경우 사용자 ID 추가
          bankName: kycData?.userData?.bankName || "",
          accountNumber: kycData?.userData?.accountNumber || "",
          accountHolder: kycData?.userData?.accountHolder || ""
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('출금 요청 완료:', data);
        message.success('수동 출금 요청이 완료되었습니다.');
        form.resetFields();
        setSelectedUser(null);
        setKycData(null);
        onSuccess();
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: 출금 요청 실패`);
      }
    } catch (error) {
      console.error('출금 요청 실패:', error);
      message.error(error instanceof Error ? error.message : '출금 요청에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 모달이 열릴 때 USDT 가격 조회
  useEffect(() => {
    if (isOpen) {
      getBithumbUSDTPrice();
    }
  }, [isOpen]);

  // 모달 닫기 시 상태 초기화
  const handleClose = () => {
    form.resetFields();
    setSelectedUser(null);
    setKycData(null);
    setInputUserId('');
    onClose();
  };

  return (
    <Modal
      title="수동 출금 요청"
      open={isOpen}
      onCancel={handleClose}
      width={800}
      footer={null}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: 20 }}
      >
        {/* 사용자 ID 직접 입력 */}
        <Form.Item label="사용자 ID" required>
          <Space.Compact style={{ width: '100%' }}>
                         <Input
               placeholder="사용자 ID를 입력하세요"
               value={inputUserId}
               onChange={(e) => {
                 setInputUserId(e.target.value);
                 // 입력값이 변경되면 기존 선택된 사용자 정보 초기화
                 if (selectedUser) {
                   setSelectedUser(null);
                   setKycData(null);
                 }
               }}
               style={{ flex: 1 }}
               onPressEnter={handleUserSearch}
             />
             <Button 
               type="primary" 
               icon={<SearchOutlined />}
               loading={searchLoading}
               onClick={handleUserSearch}
             >
              조회
            </Button>
          </Space.Compact>
        </Form.Item>

        {/* 조회된 사용자 정보 */}
        {selectedUser && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Title level={5}>조회된 사용자 정보</Title>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>사용자 ID: </Text>
                <Text>{selectedUser.id}</Text>
              </Col>
              <Col span={12}>
                <Text strong>닉네임: </Text>
                <Text>{selectedUser.nickname}</Text>
              </Col>
              <Col span={12}>
                <Text strong>이름: </Text>
                <Text>{selectedUser.fullname}</Text>
              </Col>
              <Col span={12}>
                <Text strong>레벨: </Text>
                <Text>{getLevelName(selectedUser.level)} (Level {selectedUser.level})</Text>
              </Col>
              <Col span={12}>
                <Text strong>현물지갑 잔액: </Text>
                <Text type={selectedUser.balance && selectedUser.balance > 0 ? 'success' : 'warning'}>
                  {selectedUser.balance ? Math.floor(selectedUser.balance).toLocaleString() : 0} USDT
                </Text>
              </Col>
            </Row>
          </Card>
        )}

        {/* KYC 및 은행 정보 */}
        {selectedUser && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Title level={5}>KYC 및 은행 정보</Title>
            {kycData ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>KYC 상태: </Text>
                  <Text type={kycData.status === 2 ? 'success' : 'danger'}>
                    {kycData.status === 2 ? '인증 완료' : '인증 미완료'}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text strong>예금주: </Text>
                  <Text>{kycData.userData.accountHolder || '정보 없음'}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>은행명: </Text>
                  <Text>{kycData.userData.bankName || '정보 없음'}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>계좌번호: </Text>
                  <Text>{kycData.userData.accountNumber || '정보 없음'}</Text>
                </Col>
              </Row>
            ) : (
              <Text type="warning">은행 정보가 등록되지 않았습니다.</Text>
            )}
          </Card>
        )}

        <Divider />

        {/* 현재 USDT 가격 표시 */}
        {usdtPrice > 0 && (
          <Form.Item label="현재 USDT 가격">
            <Input
              value={`1 USDT = ${usdtPrice.toLocaleString()} 원`}
              disabled
              style={{ fontWeight: 'bold', color: '#1890ff' }}
            />
          </Form.Item>
        )}

        {/* 출금 금액 입력 (원화) */}
        <Form.Item
          label="출금 금액 (원화)"
          name="krwAmount"
          rules={[
            { required: true, message: '출금 금액을 입력해주세요.' },
            { type: 'number', min: 10000, message: '최소 출금 금액은 10,000원입니다.' },
            {
              validator: (_, value) => {
                if (!selectedUser?.balance || !usdtPrice) return Promise.resolve();
                const requiredUsdt = convertKrwToUsdt(value || 0);
                if (value && requiredUsdt > Math.floor(selectedUser.balance)) {
                  return Promise.reject(new Error(`잔액을 초과할 수 없습니다. (잔액: ${Math.floor(selectedUser.balance).toLocaleString()} USDT)`));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="출금할 원화 금액"
            min={10000}
            precision={0}
            formatter={(value) => {
              if (!value) return '';
              return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }}
            parser={(value) => value?.replace(/,/g, '') as any}
            onChange={handleKrwAmountChange}
            addonAfter="원"
          />
        </Form.Item>

        {/* 변환된 USDT 금액 표시 */}
        <Form.Item
          label="변환된 USDT 금액"
          name="usdtAmount"
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder="자동 계산됨"
            precision={0}
            disabled
            formatter={(value) => {
              if (!value) return '';
              const num = parseInt(value.toString());
              return num.toLocaleString();
            }}
            addonAfter="USDT"
          />
        </Form.Item>

        {/* 제출 버튼 */}
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading} disabled={!selectedUser}>
              출금 요청
            </Button>
            <Button onClick={handleClose}>
              취소
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ManualWithdrawalModal; 